import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import SectionHelp from '@/components/help/SectionHelp';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import helpContent from '@/help/helpContent';
import { UserCog, Users, BarChart3, Clock, RefreshCw, Plus, Trash2, Layers, Route, Play, CalendarDays, AlertTriangle } from 'lucide-react';

const STRATEGIES = [
  { value: 'manual_only', label: 'Solo manual', desc: 'Asignación exclusivamente manual.' },
  { value: 'round_robin', label: 'Round Robin', desc: 'Turno rotativo entre agentes elegibles.' },
  { value: 'least_open_leads', label: 'Menos carga', desc: 'Al agente con menos leads activos.' },
];

const STICKY_SCOPES = [
  { value: 'tenant', label: 'Todo el negocio', desc: 'El mismo vendedor se mantiene sin importar por qué sucursal, medio o campaña regrese el cliente.' },
  { value: 'branch', label: 'Por sucursal', desc: 'Solo se mantiene si el cliente regresa a la MISMA sucursal; si escribe desde otra, entra al reparto normal.' },
  { value: 'campaign', label: 'Por campaña', desc: 'Solo se mantiene si el cliente regresa por la MISMA campaña/anuncio; si cambia de campaña, entra al reparto normal.' },
];

const WEEKDAYS = [
  { value: 0, label: 'L' }, { value: 1, label: 'M' }, { value: 2, label: 'M' },
  { value: 3, label: 'J' }, { value: 4, label: 'V' }, { value: 5, label: 'S' }, { value: 6, label: 'D' },
];
const WEEKDAY_FULL = { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' };

function GuardShiftsSection({ teamMembers }) {
  const [shiftsByUser, setShiftsByUser] = useState({});
  const [shiftMeta, setShiftMeta] = useState({});
  const [locations, setLocations] = useState([]);
  const [gaps, setGaps] = useState([]);
  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAbsenceForm, setShowAbsenceForm] = useState(false);
  const [absenceForm, setAbsenceForm] = useState({ user_id: '', date_from: '', date_to: '', reason: '', replacement_user_id: '' });

  const load = async () => {
    try {
      const [sRes, aRes] = await Promise.all([
        api.get('/tenant/assignment/guard-shifts'),
        api.get('/tenant/assignment/guard-absences'),
      ]);
      const map = {};
      const meta = {};
      (sRes.data?.shifts || []).forEach(s => {
        map[s.user_id] = s.weekdays;
        meta[s.user_id] = {
          start_time: s.start_time || '',
          end_time: s.end_time || '',
          branch_id: s.branch_id || '',
        };
      });
      setShiftsByUser(map);
      setShiftMeta(meta);
      setGaps(sRes.data?.gaps || []);
      setAbsences(aRes.data || []);
    } catch { toast.error('Error cargando guardias'); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    api.get('/tenant/locations').then(r => setLocations(r.data?.items || r.data || [])).catch(() => {});
  }, []);

  const saveShifts = async (userId, weekdays) => {
    const meta = shiftMeta[userId] || {};
    await api.put('/tenant/assignment/guard-shifts', {
      user_id: userId,
      weekdays,
      start_time: meta.start_time || null,
      end_time: meta.end_time || null,
      branch_id: meta.branch_id || null,
    });
  };

  const toggleDay = async (userId, weekday) => {
    const current = shiftsByUser[userId] || [];
    const next = current.includes(weekday) ? current.filter(d => d !== weekday) : [...current, weekday];
    try {
      await saveShifts(userId, next);
      setShiftsByUser(s => ({ ...s, [userId]: next }));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const updateMeta = async (userId, patch) => {
    const nextMeta = { ...(shiftMeta[userId] || { start_time: '', end_time: '', branch_id: '' }), ...patch };
    setShiftMeta(s => ({ ...s, [userId]: nextMeta }));
    try {
      await saveShifts(userId, shiftsByUser[userId] || []);
      toast.success('Horario de guardia guardado');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const createAbsence = async () => {
    if (!absenceForm.user_id || !absenceForm.date_from || !absenceForm.date_to) { toast.error('Completa persona y fechas'); return; }
    try {
      await api.post('/tenant/assignment/guard-absences', {
        ...absenceForm,
        replacement_user_id: absenceForm.replacement_user_id || null,
      });
      toast.success('Ausencia registrada');
      setShowAbsenceForm(false);
      setAbsenceForm({ user_id: '', date_from: '', date_to: '', reason: '', replacement_user_id: '' });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const deleteAbsence = async (id) => {
    try { await api.delete(`/tenant/assignment/guard-absences/${id}`); toast.success('Ausencia eliminada'); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const memberName = (uid) => teamMembers.find(m => (m.id || m.user_id) === uid)?.name || 'Alguien';

  if (loading) return null;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4" data-testid="guard-shifts-section">
      <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><CalendarDays className="w-4 h-4" /> Guardias</h3>
      <p className="text-xs text-[#64748B]">
        Marca qué días tiene guardia cada persona. Opcionalmente limita el horario (ej. 9:00–14:00) o una sede.
        Déjalo vacío para cubrir el día completo y todas las sedes — mismo comportamiento de antes.
      </p>

      {gaps.length > 0 && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2" data-testid="guard-gaps-warning">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">Sin nadie de guardia: {gaps.map(g => WEEKDAY_FULL[g]).join(', ')}</p>
        </div>
      )}

      <div className="space-y-2">
        {teamMembers.filter(m => m.is_active !== false).map(m => {
          const uid = m.id || m.user_id;
          const days = shiftsByUser[uid] || [];
          return (
            <div key={uid} className="py-2 border-b border-[#F1F5F9] last:border-0 space-y-2" data-testid={`guard-row-${uid}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-[#0F172A] font-medium w-32 truncate">{m.name || m.email}</span>
                <div className="flex gap-1">
                  {WEEKDAYS.map(d => (
                    <button key={d.value} onClick={() => toggleDay(uid, d.value)}
                      className={`w-7 h-7 rounded-full text-xs font-medium border transition-all ${
                        days.includes(d.value) ? 'bg-[#0F172A] text-white border-[#0F172A]' : 'border-[#E2E8F0] text-[#94A3B8] hover:border-[#94A3B8]'
                      }`} data-testid={`guard-day-${uid}-${d.value}`}>
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-32">
                <Input
                  type="time"
                  className="h-8 w-[110px] text-xs"
                  title="Hora de inicio — vacío = todo el día"
                  value={(shiftMeta[uid]?.start_time) || ''}
                  onChange={e => setShiftMeta(s => ({ ...s, [uid]: { ...(s[uid] || {}), start_time: e.target.value } }))}
                  onBlur={() => updateMeta(uid, shiftMeta[uid] || {})}
                  data-testid={`guard-start-${uid}`}
                />
                <span className="text-xs text-[#94A3B8]">a</span>
                <Input
                  type="time"
                  className="h-8 w-[110px] text-xs"
                  title="Hora de fin — vacío = todo el día"
                  value={(shiftMeta[uid]?.end_time) || ''}
                  onChange={e => setShiftMeta(s => ({ ...s, [uid]: { ...(s[uid] || {}), end_time: e.target.value } }))}
                  onBlur={() => updateMeta(uid, shiftMeta[uid] || {})}
                  data-testid={`guard-end-${uid}`}
                />
                <Select
                  value={(shiftMeta[uid]?.branch_id) || 'all'}
                  onValueChange={v => updateMeta(uid, { branch_id: v === 'all' ? '' : v })}
                >
                  <SelectTrigger className="h-8 w-[160px] text-xs" title="Sede — vacío = todas las sedes" data-testid={`guard-branch-${uid}`}>
                    <SelectValue placeholder="Todas las sedes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las sedes</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
        {teamMembers.length === 0 && <p className="text-xs text-[#94A3B8] text-center py-3">Agrega miembros a tu equipo primero.</p>}
      </div>

      <div className="border-t border-[#E2E8F0] pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-[#0F172A]">Ausencias y reemplazos</p>
          <Button variant="outline" size="sm" onClick={() => setShowAbsenceForm(!showAbsenceForm)} data-testid="add-absence-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Nueva</Button>
        </div>
        {showAbsenceForm && (
          <div className="p-3 bg-[#F8F9FA] rounded-lg space-y-2">
            <Select value={absenceForm.user_id} onValueChange={v => setAbsenceForm(f => ({ ...f, user_id: v }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="absence-user-select"><SelectValue placeholder="Persona ausente" /></SelectTrigger>
              <SelectContent>{teamMembers.map(m => <SelectItem key={m.id || m.user_id} value={m.id || m.user_id}>{m.name || m.email}</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" className="h-8 text-xs" value={absenceForm.date_from} onChange={e => setAbsenceForm(f => ({ ...f, date_from: e.target.value }))} data-testid="absence-date-from" />
              <Input type="date" className="h-8 text-xs" value={absenceForm.date_to} onChange={e => setAbsenceForm(f => ({ ...f, date_to: e.target.value }))} data-testid="absence-date-to" />
            </div>
            <Input placeholder="Razón (opcional)" className="h-8 text-xs" value={absenceForm.reason} onChange={e => setAbsenceForm(f => ({ ...f, reason: e.target.value }))} data-testid="absence-reason" />
            <Select value={absenceForm.replacement_user_id} onValueChange={v => setAbsenceForm(f => ({ ...f, replacement_user_id: v }))}>
              <SelectTrigger className="h-8 text-xs" data-testid="absence-replacement-select"><SelectValue placeholder="¿Quién la cubre? (opcional)" /></SelectTrigger>
              <SelectContent>{teamMembers.filter(m => (m.id || m.user_id) !== absenceForm.user_id).map(m => <SelectItem key={m.id || m.user_id} value={m.id || m.user_id}>{m.name || m.email}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowAbsenceForm(false)}>Cancelar</Button>
              <Button size="sm" className="h-7 text-xs bg-[#0F172A] text-white" onClick={createAbsence} data-testid="save-absence-btn">Guardar</Button>
            </div>
          </div>
        )}
        {absences.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">Sin ausencias registradas.</p>
        ) : (
          <div className="space-y-1">
            {absences.map(a => (
              <div key={a.id} className="flex items-center justify-between text-xs py-1.5 border-b border-[#F1F5F9] last:border-0" data-testid={`absence-${a.id}`}>
                <span>
                  <strong className="text-[#0F172A]">{memberName(a.user_id)}</strong>
                  <span className="text-[#94A3B8]"> {a.date_from} a {a.date_to}</span>
                  {a.replacement_user_id && <span className="text-emerald-600"> · cubre {memberName(a.replacement_user_id)}</span>}
                </span>
                <button onClick={() => deleteAbsence(a.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Sprint 53 (7/N) — valores reales que usan las Fuentes de Leads en este
// sistema (LeadSource.medium/channel) — sugeridos vía <datalist>, no un
// <select> rígido, porque un tenant puede tener fuentes con valores propios
// que no están en esta lista corta.
const KNOWN_MEDIUMS = ['whatsapp', 'email', 'sms', 'voice', 'web', 'telegram'];
const KNOWN_CHANNELS = ['web', 'facebook', 'instagram', 'google_ads', 'referral', 'direct', 'whatsapp'];

// Sprint 53 (12/N) — antes eran campos de texto libre separados por coma
// ("de verdad es un ux de porquería" per feedback del usuario). Ahora medio/
// canal son checkboxes reales con los valores que el sistema de verdad
// reconoce (no una sugerencia que se puede escribir mal), y campañas/
// códigos son chips (escribes y presionas Enter) en vez de CSV a mano.
function TagInput({ values, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState('');
  const addTag = () => {
    const v = draft.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setDraft('');
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1 mb-1">
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 bg-[#F1F5F9] text-[#334155] text-[10px] px-2 py-0.5 rounded-full">
            {v}
            <button type="button" onClick={() => onChange(values.filter(x => x !== v))} className="text-[#94A3B8] hover:text-red-600">×</button>
          </span>
        ))}
      </div>
      <Input
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
        onBlur={addTag}
        placeholder={placeholder}
        className="h-7 text-xs"
        data-testid={testId}
      />
    </div>
  );
}

const EMPTY_RULE_FORM = { name: '', priority: '100', mediums: [], channels: [], campaign_names: [], campaign_codes: [], match_mode: 'all' };

function PoolRulesSection({ pool }) {
  const [rules, setRules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState(null);
  const [form, setForm] = useState(EMPTY_RULE_FORM);

  const loadRules = async () => {
    try { const res = await api.get(`/tenant/assignment/pools/${pool.id}/rules`); setRules(res.data?.items || []); } catch {}
  };
  useEffect(() => { loadRules(); }, [pool.id]);

  const priorityValid = /^\d+$/.test(String(form.priority).trim());

  const toggleValue = (key, val) => {
    setForm(f => ({ ...f, [key]: f[key].includes(val) ? f[key].filter(v => v !== val) : [...f[key], val] }));
  };

  const cancelForm = () => { setForm(EMPTY_RULE_FORM); setEditingRuleId(null); setShowForm(false); };

  const startEdit = (r) => {
    setForm({
      name: r.name, priority: String(r.priority ?? 100),
      mediums: r.mediums || [], channels: r.channels || [],
      campaign_names: r.campaign_names || [], campaign_codes: r.campaign_codes || [],
      match_mode: r.match_mode || 'all',
    });
    setEditingRuleId(r.id);
    setShowForm(true);
  };

  const saveRule = async () => {
    if (!form.name.trim()) { toast.error('Falta el nombre de la regla'); return; }
    if (!priorityValid) { toast.error('La prioridad debe ser un número entero'); return; }
    const payload = {
      name: form.name, priority: parseInt(form.priority),
      mediums: form.mediums, channels: form.channels,
      campaign_names: form.campaign_names, campaign_codes: form.campaign_codes,
      match_mode: form.match_mode,
    };
    try {
      if (editingRuleId) {
        await api.put(`/tenant/assignment/pools/${pool.id}/rules/${editingRuleId}`, payload);
        toast.success('Regla actualizada');
      } else {
        await api.post(`/tenant/assignment/pools/${pool.id}/rules`, payload);
        toast.success('Regla creada');
      }
      cancelForm();
      loadRules();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const deleteRule = async (ruleId) => {
    try { await api.delete(`/tenant/assignment/pools/${pool.id}/rules/${ruleId}`); toast.success('Regla eliminada'); loadRules(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  return (
    <div className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`pool-rules-section-${pool.key}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-bold text-[#0F172A] flex items-center gap-1"><Route className="w-3 h-3" /> Reglas: {pool.name}</p>
        <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => (showForm ? cancelForm() : setShowForm(true))}><Plus className="w-3 h-3 mr-0.5" /> Regla</Button>
      </div>
      <p className="text-[10px] text-[#64748B] leading-relaxed">
        Cuando llega un lead, se evalúan estas reglas por prioridad (número más alto gana) — la primera que coincida con el medio/canal/campaña del lead decide a qué equipo se enruta. Si ninguna coincide, el lead no se asigna a un equipo específico.
      </p>
      {rules.map(r => (
        <div key={r.id} className="flex items-center gap-2 text-[10px] py-1 border-b border-[#F1F5F9] last:border-0">
          <span className={`w-1.5 h-1.5 rounded-full ${r.is_active ? 'bg-emerald-400' : 'bg-gray-300'}`} />
          <span className="font-medium text-[#0F172A]">{r.name}</span>
          <span className="text-[#94A3B8]">P{r.priority}</span>
          {r.mediums?.length > 0 && <span className="text-[#94A3B8]">med: {r.mediums.join(', ')}</span>}
          {r.campaign_names?.length > 0 && <span className="text-[#94A3B8]">camp: {r.campaign_names.join(', ')}</span>}
          <button onClick={() => startEdit(r)} className="ml-auto text-[#64748B] hover:text-[#0F172A]">Editar</button>
          <SensitiveActionHelp action={sensitiveActions.assignment_pool_rule_delete} onConfirm={() => deleteRule(r.id)}>
            <button className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
          </SensitiveActionHelp>
        </div>
      ))}
      {rules.length === 0 && <p className="text-[10px] text-[#94A3B8]">Sin reglas de enrutamiento.</p>}
      {showForm && (
        <div className="space-y-2 pt-1 border-t border-[#E2E8F0] mt-1">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-[10px] text-[#64748B]">Nombre</label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="h-7 text-xs" /></div>
            <div>
              <label className="text-[10px] text-[#64748B]">Prioridad {!priorityValid && <span className="text-red-500">(debe ser un número)</span>}</label>
              <Input type="number" value={form.priority} onChange={e => setForm({...form, priority: e.target.value})} className="h-7 text-xs" />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Medio (por dónde llegó el lead) — marca los que apliquen</label>
            <div className="flex flex-wrap gap-2">
              {KNOWN_MEDIUMS.map(m => (
                <label key={m} className="inline-flex items-center gap-1 text-[11px] text-[#334155]">
                  <input type="checkbox" checked={form.mediums.includes(m)} onChange={() => toggleValue('mediums', m)} /> {m}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Canal (origen específico) — marca los que apliquen</label>
            <div className="flex flex-wrap gap-2">
              {KNOWN_CHANNELS.map(c => (
                <label key={c} className="inline-flex items-center gap-1 text-[11px] text-[#334155]">
                  <input type="checkbox" checked={form.channels.includes(c)} onChange={() => toggleValue('channels', c)} /> {c}
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Nombre de campaña — el nombre visible que le pusiste (ej. "Promo Verano"). Escribe y presiona Enter para agregar.</label>
            <TagInput values={form.campaign_names} onChange={v => setForm({...form, campaign_names: v})} placeholder="Promo Verano" testId="rule-campaign-names-input" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B] block mb-1">Código de campaña — el código corto/UTM que identifica la campaña (ej. "verano24"), si lo usas. Escribe y presiona Enter.</label>
            <TagInput values={form.campaign_codes} onChange={v => setForm({...form, campaign_codes: v})} placeholder="verano24" testId="rule-campaign-codes-input" />
          </div>
          <div>
            <label className="text-[10px] text-[#64748B]">Cómo deben coincidir los campos de arriba</label>
            <select value={form.match_mode} onChange={e => setForm({...form, match_mode: e.target.value})} className="w-full h-7 text-xs border rounded px-2">
              <option value="all">Todos los campos que marcaste/llenaste deben coincidir</option>
              <option value="any">Basta con que uno solo coincida</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={cancelForm}>Cancelar</Button>
            <Button size="sm" className="h-6 text-[10px]" onClick={saveRule}>{editingRuleId ? 'Guardar cambios' : 'Crear regla'}</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function RoutingSimulator() {
  const [form, setForm] = useState({ medium: '', channel: '', campaign_name: '', campaign_code: '' });
  const [result, setResult] = useState(null);
  const simulate = async () => {
    try {
      const res = await api.post('/tenant/assignment/routing/simulate', form);
      setResult(res.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error simulación'); }
  };
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="pool-rule-simulate">
      <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><Play className="w-4 h-4" /> Simulador de enrutamiento</h3>
      {/* Sprint 53 (7/N) — este copy ya existía en sensitiveActions.js pero
          nunca se mostraba junto al botón Simular. */}
      <p className="text-xs text-[#64748B] leading-relaxed">
        {sensitiveActions.assignment_routing_simulate.warning} {sensitiveActions.assignment_routing_simulate.details}
      </p>
      <datalist id="assignment-mediums-list-sim">{KNOWN_MEDIUMS.map(m => <option key={m} value={m} />)}</datalist>
      <datalist id="assignment-channels-list-sim">{KNOWN_CHANNELS.map(c => <option key={c} value={c} />)}</datalist>
      <div className="grid grid-cols-2 gap-2">
        <div><label className="text-xs text-[#64748B]">Medio</label><Input list="assignment-mediums-list-sim" value={form.medium} onChange={e => setForm({...form, medium: e.target.value})} placeholder="whatsapp" className="h-8 text-xs" /></div>
        <div><label className="text-xs text-[#64748B]">Canal</label><Input list="assignment-channels-list-sim" value={form.channel} onChange={e => setForm({...form, channel: e.target.value})} placeholder="web" className="h-8 text-xs" /></div>
        <div><label className="text-xs text-[#64748B]">Campaña</label><Input value={form.campaign_name} onChange={e => setForm({...form, campaign_name: e.target.value})} placeholder="Promo Verano" className="h-8 text-xs" /></div>
        <div><label className="text-xs text-[#64748B]">Código</label><Input value={form.campaign_code} onChange={e => setForm({...form, campaign_code: e.target.value})} className="h-8 text-xs" /></div>
      </div>
      <Button size="sm" onClick={simulate}><Play className="w-3.5 h-3.5 mr-1" /> Simular</Button>
      {result && (
        <div className={`p-3 rounded-lg border text-xs ${result.matched ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
          <p className="font-medium">{result.matched ? `Equipo (pool): ${result.pool_name}` : 'Sin coincidencia — ningún equipo se enrutaría con estos valores'}</p>
          <p className="text-[#64748B]">{result.reason}</p>
          {result.rule_name && <p className="text-[#94A3B8]">Regla que ganó: {result.rule_name}</p>}
          <p className="text-[10px] text-[#94A3B8] italic mt-1.5 pt-1.5 border-t border-[#F1F5F9]">
            Esto solo muestra a qué equipo se enrutaría el lead — no muestra qué vendedor específico lo recibiría (eso depende además de guardias/round-robin/carga, que aquí no se simulan).
          </p>
        </div>
      )}
    </div>
  );
}

const DURATION_UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440, months: 43200 };
const DURATION_UNITS = [
  { value: 'minutes', label: 'Minutos' },
  { value: 'hours', label: 'Horas' },
  { value: 'days', label: 'Días' },
  { value: 'months', label: 'Meses' },
];

function minutesToValueUnit(minutes) {
  if (!minutes) return { value: '', unit: 'hours' };
  for (const unit of ['months', 'days', 'hours']) {
    const factor = DURATION_UNIT_MINUTES[unit];
    if (minutes % factor === 0) return { value: String(minutes / factor), unit };
  }
  return { value: String(minutes), unit: 'minutes' };
}

export default function CrmAssignment() {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [pools, setPools] = useState([]);
  const [presets, setPresets] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPoolForm, setShowPoolForm] = useState(false);
  const [poolForm, setPoolForm] = useState({ key: '', name: '', description: '' });
  const [contactRules, setContactRules] = useState({});
  const [threadDurationValue, setThreadDurationValue] = useState('');
  const [threadDurationUnit, setThreadDurationUnit] = useState('hours');
  const [savingThreadDuration, setSavingThreadDuration] = useState(false);

  const load = async () => {
    try {
      const [cRes, sRes, lRes, pRes, prRes, tRes, profRes] = await Promise.all([
        api.get('/tenant/assignment/config'),
        api.get('/tenant/assignment/status'),
        api.get('/tenant/assignment/logs?limit=20'),
        api.get('/tenant/assignment/pools'),
        api.get('/tenant/assignment/pool-presets'),
        api.get('/tenant/team').catch(() => ({ data: [] })),
        api.get('/tenant/profile').catch(() => ({ data: {} })),
      ]);
      setConfig(cRes.data);
      setStatus(sRes.data);
      setLogs(lRes.data?.items || []);
      setPools(pRes.data?.items || []);
      setPresets(prRes.data?.presets || []);
      setTeamMembers(Array.isArray(tRes.data) ? tRes.data : tRes.data?.items || []);
      const rules = profRes.data?.contact_rules || {};
      setContactRules(rules);
      const { value, unit } = minutesToValueUnit(rules.conversation_thread_duration_minutes);
      setThreadDurationValue(value); setThreadDurationUnit(unit);
    } catch { toast.error('Error cargando configuración'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveThreadDuration = async () => {
    setSavingThreadDuration(true);
    try {
      const minutes = threadDurationValue ? Math.round(Number(threadDurationValue) * DURATION_UNIT_MINUTES[threadDurationUnit]) : null;
      const nextRules = { ...contactRules, conversation_thread_duration_minutes: minutes };
      await api.put('/tenant/profile', { contact_rules: nextRules });
      setContactRules(nextRules);
      toast.success('Duración del hilo guardada');
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar'); }
    setSavingThreadDuration(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/assignment/config', config);
      toast.success('Configuración guardada');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al guardar'); }
    setSaving(false);
  };

  const createPoolFromPreset = async (presetKey) => {
    try {
      await api.post('/tenant/assignment/pools', { preset_key: presetKey });
      toast.success('Equipo creado desde preset');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const createCustomPool = async () => {
    if (!poolForm.key || !poolForm.name) { toast.error('Clave y nombre requeridos'); return; }
    try {
      await api.post('/tenant/assignment/pools', poolForm);
      toast.success('Equipo custom creado');
      setPoolForm({ key: '', name: '', description: '' });
      setShowPoolForm(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const deletePool = async (poolId) => {
    try {
      await api.delete(`/tenant/assignment/pools/${poolId}`);
      toast.success('Equipo eliminado');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  const updatePoolUsers = async (poolId, userIds, isDefault) => {
    try {
      const pool = pools.find(p => p.id === poolId);
      await api.put(`/tenant/assignment/pools/${poolId}`, {
        eligible_user_ids: userIds,
        is_default: isDefault ?? pool?.is_default ?? false,
        is_active: pool?.is_active ?? true,
      });
      toast.success('Equipo actualizado');
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
  };

  if (loading) return <PantallaCargando />;

  const strategyInfo = STRATEGIES.find(s => s.value === config?.strategy) || STRATEGIES[0];
  const usedPresetKeys = pools.filter(p => p.preset_key).map(p => p.preset_key);
  const availablePresets = presets.filter(p => !usedPresetKeys.includes(p.key));

  return (
    <div className="space-y-5" data-testid="crm-assignment">
      {/* Sprint 53 (7/N) — este copy ya existía en helpContent.js pero nunca
          se pasaba como prop, así que SectionHelp renderizaba "¿Cómo funciona
          undefined?" sin contenido. */}
      <SectionHelp module="assignment_config" {...helpContent.assignment_config} />

      {/* Config Card */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4" data-testid="assignment-config-card">
        <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
          <UserCog className="w-4 h-4" /> Motor de asignación
        </h3>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Habilitado</p><p className="text-xs text-[#64748B]">Activa la asignación automática.</p></div>
          <Switch checked={config?.is_enabled || false} onCheckedChange={v => setConfig({ ...config, is_enabled: v })} data-testid="assignment-toggle-enabled" />
        </div>
        <div className="space-y-1">
          <label className="text-sm font-medium">Estrategia</label>
          <Select value={config?.strategy || 'manual_only'} onValueChange={v => setConfig({ ...config, strategy: v })}>
            <SelectTrigger data-testid="assignment-strategy-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STRATEGIES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="text-xs text-[#64748B]">{strategyInfo.desc}</p>
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Auto-asignar al convertir inbound</p></div>
          <Switch checked={config?.auto_on_inbound_convert ?? true} onCheckedChange={v => setConfig({ ...config, auto_on_inbound_convert: v })} data-testid="assignment-toggle-inbound" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Auto-asignar en lead manual</p></div>
          <Switch checked={config?.auto_on_manual_lead ?? true} onCheckedChange={v => setConfig({ ...config, auto_on_manual_lead: v })} data-testid="assignment-toggle-manual" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Auto-match por interés a equipo</p><p className="text-xs text-[#64748B]">Match automático de leads a equipos por palabras clave.</p></div>
          <Switch checked={config?.auto_pool_match ?? false} onCheckedChange={v => setConfig({ ...config, auto_pool_match: v })} data-testid="assignment-toggle-pool-match" />
        </div>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Usar guardias para asignar</p><p className="text-xs text-[#64748B]">Solo quien tenga guardia ese día recibe leads. Si nadie tiene guardia, se asigna igual a todo el equipo elegible.</p></div>
          <Switch checked={config?.use_guard_shifts ?? false} onCheckedChange={v => setConfig({ ...config, use_guard_shifts: v })} data-testid="assignment-toggle-guard-shifts" />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Llamada automática al vendedor (IVR) al asignar</p>
            <p className="text-xs text-[#64748B]">Cuando un lead se asigna por primera vez a un vendedor, el sistema hace una llamada: marca al lead, le pide «presiona 1» y lo conecta con su vendedor. <strong>Apagado por defecto.</strong> Enciéndelo cuando quieras que ese primer contacto sea por teléfono automático. (Es el mismo interruptor que el nodo «Conectar lead con vendedor» del Constructor de flujos.)</p>
          </div>
          <Switch checked={config?.auto_ivr_on_first_link ?? false} onCheckedChange={v => setConfig({ ...config, auto_ivr_on_first_link: v })} data-testid="assignment-toggle-ivr" />
        </div>
        <div className="flex justify-end">
          <Button size="sm" onClick={save} disabled={saving} data-testid="assignment-save-config">{saving ? 'Guardando...' : 'Guardar configuración'}</Button>
        </div>
      </div>

      {/* Sticky assignment ("duplicidad") */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4" data-testid="sticky-assignment-card">
        <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
          <RefreshCw className="w-4 h-4" /> Mismo vendedor para clientes recurrentes
        </h3>
        <p className="text-xs text-[#64748B]">
          Si un cliente que ya fue atendido antes (mismo teléfono o correo) vuelve a escribir o llamar, se le asigna
          de nuevo al MISMO vendedor que ya lo atendió, en vez de entrar otra vez al reparto (round robin). Así no se
          pierde el seguimiento ni la relación que ya tenía con ese vendedor.
        </p>
        <div className="flex items-center justify-between">
          <div><p className="text-sm font-medium">Activar</p></div>
          <Switch checked={config?.sticky_assignment_enabled ?? false}
                  onCheckedChange={v => setConfig({ ...config, sticky_assignment_enabled: v })}
                  data-testid="assignment-toggle-sticky" />
        </div>
        {config?.sticky_assignment_enabled && (
          <>
            <div className="space-y-1">
              <label className="text-sm font-medium">¿En qué casos se mantiene el mismo vendedor?</label>
              <Select value={config?.sticky_assignment_scope || 'tenant'} onValueChange={v => setConfig({ ...config, sticky_assignment_scope: v })}>
                <SelectTrigger data-testid="sticky-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STICKY_SCOPES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#64748B]">{STICKY_SCOPES.find(s => s.value === config?.sticky_assignment_scope)?.desc}</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Días sin actividad para que expire</label>
              <Input type="number" min="0" placeholder="Vacío = nunca expira"
                     value={config?.sticky_assignment_expire_days ?? ''}
                     onChange={e => setConfig({ ...config, sticky_assignment_expire_days: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                     className="h-8 text-xs w-40" data-testid="sticky-expire-days-input" />
              <p className="text-xs text-[#64748B]">Si el cliente no ha tenido actividad en más de estos días, se libera y vuelve al reparto normal. Déjalo vacío para que nunca expire.</p>
            </div>
          </>
        )}
      </div>

      {/* Conversation thread duration */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="thread-duration-card">
        <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
          <Clock className="w-4 h-4" /> Duración del hilo de conversación
        </h3>
        <p className="text-xs text-[#64748B]">
          Cuánto tiempo sin que el cliente escriba debe pasar antes de considerar que su conversación de WhatsApp
          terminó. Al reescribir después de ese tiempo, se abre un hilo nuevo para ese mismo canal (visible como su
          propia pestaña en la ficha del lead) — pero sigue siendo el mismo lead, no uno nuevo. Déjalo vacío para
          que un hilo nunca expire por tiempo.
        </p>
        <div className="flex items-center gap-2">
          <Input type="number" min="0" placeholder="Vacío = nunca expira" value={threadDurationValue}
                 onChange={e => setThreadDurationValue(e.target.value)}
                 className="h-8 text-xs w-32" data-testid="thread-duration-value-input" />
          <Select value={threadDurationUnit} onValueChange={setThreadDurationUnit}>
            <SelectTrigger className="h-8 text-xs w-32" data-testid="thread-duration-unit-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {DURATION_UNITS.map(u => <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={saveThreadDuration} disabled={savingThreadDuration} data-testid="thread-duration-save">
            {savingThreadDuration ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </div>

      <GuardShiftsSection teamMembers={teamMembers} />

      {/* Pools Section */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4" data-testid="assignment-pools-section">
        <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2">
          <Layers className="w-4 h-4" /> Equipos de asignación
        </h3>

        {pools.length === 0 && teamMembers.length === 0 && (
          <p className="text-sm text-[#64748B]">Agrega miembros a tu equipo primero para crear equipos de asignación.</p>
        )}

        {pools.map(pool => (
          <div key={pool.id} className="border border-[#E2E8F0] rounded-lg p-3 space-y-2" data-testid={`pool-card-${pool.key}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-[#0F172A]">{pool.name} {pool.is_default && <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full ml-1">default</span>}</p>
                <p className="text-[10px] text-[#94A3B8]">{pool.key} {pool.preset_key ? '(preset)' : '(custom)'}</p>
              </div>
              <SensitiveActionHelp action={sensitiveActions.assignment_pool_delete} onConfirm={() => deletePool(pool.id)}>
                <button className="text-red-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </SensitiveActionHelp>
            </div>
            <div className="text-xs text-[#64748B]">
              Agentes: {pool.eligible_user_ids?.length || 0}
              {!pool.is_default && (
                <SensitiveActionHelp action={sensitiveActions.assignment_pool_change} onConfirm={() => updatePoolUsers(pool.id, pool.eligible_user_ids, true)}>
                  <button className="ml-2 text-blue-500 hover:underline">Marcar default</button>
                </SensitiveActionHelp>
              )}
            </div>
            {/* User assignment checkboxes */}
            {teamMembers.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {teamMembers.filter(m => m.is_active).map(m => {
                  const uid = m.id || m.user_id;
                  const checked = (pool.eligible_user_ids || []).includes(uid);
                  return (
                    <label key={uid} className={`text-[10px] px-2 py-0.5 rounded-full border cursor-pointer ${checked ? 'bg-emerald-50 border-emerald-300 text-emerald-700' : 'bg-gray-50 border-gray-200 text-gray-500'}`}>
                      <input type="checkbox" className="sr-only" checked={checked}
                        onChange={() => {
                          const newIds = checked ? pool.eligible_user_ids.filter(u => u !== uid) : [...(pool.eligible_user_ids || []), uid];
                          updatePoolUsers(pool.id, newIds, pool.is_default);
                        }} />
                      {m.name || m.email?.split('@')[0]}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* Preset seed buttons */}
        {availablePresets.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs text-[#64748B] font-medium">Crear desde preset:</p>
            <div className="flex flex-wrap gap-1">
              {availablePresets.map(p => (
                <Button key={p.key} size="sm" variant="outline" className="text-xs h-7" onClick={() => createPoolFromPreset(p.key)}>
                  <Plus className="w-3 h-3 mr-1" /> {p.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Custom pool form */}
        {showPoolForm ? (
          <div className="border rounded-lg p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="text-xs text-[#64748B]">Clave</label><Input value={poolForm.key} onChange={e => setPoolForm({...poolForm, key: e.target.value})} placeholder="mi-equipo" className="h-8 text-xs" /></div>
              <div><label className="text-xs text-[#64748B]">Nombre</label><Input value={poolForm.name} onChange={e => setPoolForm({...poolForm, name: e.target.value})} placeholder="Equipo Especial" className="h-8 text-xs" /></div>
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowPoolForm(false)}>Cancelar</Button>
              <Button size="sm" className="h-7 text-xs" onClick={createCustomPool}>Crear equipo</Button>
            </div>
          </div>
        ) : (
          <Button size="sm" variant="outline" onClick={() => setShowPoolForm(true)} data-testid="assignment-add-custom-pool">
            <Plus className="w-3.5 h-3.5 mr-1" /> Equipo custom
          </Button>
        )}
      </div>

      {/* Agents Load */}
      {status?.agents_load?.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="assignment-agents-load">
          <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Carga de agentes</h3>
          <div className="space-y-2">
            {status.agents_load.map(a => (
              <div key={a.user_id} className="flex items-center justify-between py-1.5 border-b border-[#F1F5F9] last:border-0">
                <div><p className="text-sm font-medium text-[#0F172A]">{a.name}</p><p className="text-[10px] text-[#94A3B8]">{a.email} &middot; {a.tenant_role}</p></div>
                <span className="text-sm font-bold text-[#0F172A]">{a.open_leads} <span className="text-xs font-normal text-[#64748B]">leads</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pool Rules per pool */}
      {pools.map(pool => (
        <PoolRulesSection key={pool.id} pool={pool} />
      ))}

      {/* Simulator */}
      <RoutingSimulator />

      {/* Summary Stats */}
      {status && (
        <div className="grid grid-cols-2 gap-3" data-testid="assignment-stats">
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 text-center">
            <Users className="w-4 h-4 text-amber-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-[#0F172A]">{status.unassigned_leads_count}</p>
            <p className="text-[10px] text-[#64748B]">Sin asignar</p>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-lg p-3 text-center">
            <Users className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-[#0F172A]">{status.assigned_leads_count}</p>
            <p className="text-[10px] text-[#64748B]">Asignados</p>
          </div>
        </div>
      )}

      {/* Recent Logs */}
      {logs.length > 0 && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="assignment-logs">
          <h3 className="text-sm font-bold text-[#0F172A] flex items-center gap-2"><Clock className="w-4 h-4" /> Últimas asignaciones</h3>
          <div className="space-y-1 max-h-[200px] overflow-y-auto">
            {logs.map(log => (
              <div key={log.id} className="text-xs flex items-center gap-2 py-1 border-b border-[#F1F5F9] last:border-0">
                <span className="text-[#94A3B8] w-[100px] flex-shrink-0">{log.created_at ? new Date(log.created_at).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—'}</span>
                <span className="font-medium text-[#0F172A]">{log.entity_type}</span>
                <span className="text-[#64748B] truncate">{log.summary}</span>
                <span className="text-[10px] text-[#94A3B8] ml-auto">{STRATEGIES.find(s => s.value === log.strategy)?.label || log.strategy}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex justify-center">
        <Button size="sm" variant="outline" onClick={load} data-testid="assignment-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar</Button>
      </div>
    </div>
  );
}
